// Copyright (c) 2024. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { useCallback } from "react";
import { ButtonStyle } from "../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import { Button } from "../../../../../../io/hyperify/frontend/components/button/Button";
import { Form } from "../../../../../../io/hyperify/frontend/components/form/Form";
import { LinkButton } from "../../../../../../io/hyperify/frontend/components/router/linkButton/LinkButton";
import { SubmitButton } from "../../../../../../io/hyperify/frontend/components/submitButton/SubmitButton";
import { useCookieConsent } from "../../../../../../io/hyperify/frontend/hooks/useCookieConsent";
import { CookieConsent } from "../../../../../../io/hyperify/frontend/types/CookieConsent";
import { COOKIE_CONSENT_ARTICLE_CLASS_NAME } from "../../../../core/constants/className";
import { COOKIES_ROUTE } from "../../../../core/constants/route";
import {
    T_COOKIE_CONSENT_ACCEPT_ALL_BUTTON_LABEL,
    T_COOKIE_CONSENT_ACCEPT_ALL_SELECTED_TEXT,
    T_COOKIE_CONSENT_ACCEPT_MANDATORY_BUTTON_LABEL,
    T_COOKIE_CONSENT_ACCEPT_MANDATORY_SELECTED_TEXT,
    T_COOKIE_CONSENT_ARTICLE_CONTENT,
    T_COOKIE_CONSENT_ARTICLE_TITLE,
    T_COOKIE_CONSENT_SETTINGS_BUTTON_LABEL,
} from "../../../../core/constants/translation";
import "./CookieConsentArticle.scss";

export interface CookieConsentArticleProps {
    readonly className ?: string;
    readonly t : TranslationFunction;
    readonly enableSettingsButton ?: boolean;
}

export function CookieConsentArticle (props: CookieConsentArticleProps) {
    const className = props?.className;
    const enableSettingsButton = props?.enableSettingsButton ?? false;
    const t = props.t;
    const [cookieConsent, setCookieConsent] = useCookieConsent();

    const acceptAll = useCallback(
        () => {
            setCookieConsent(CookieConsent.ACCEPT_ALL);
        }, [
            setCookieConsent
        ],
    );

    const acceptMandatory = useCallback(
        () => {
            setCookieConsent(CookieConsent.ACCEPT_MANDATORY);
        }, [
            setCookieConsent
        ],
    );

    // const removeConsent = useCallback(
    //     () => {
    //         setCookieConsent(CookieConsent.UNDEFINED);
    //     }, [
    //         setCookieConsent
    //     ],
    // );

    return (
        <div className={
            COOKIE_CONSENT_ARTICLE_CLASS_NAME
            + (className? ` ${className}` : '')
        }>
            <Form className={`${COOKIE_CONSENT_ARTICLE_CLASS_NAME}-form`}
                  submit={acceptAll}
            >
                <div className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+'-section'}>
                    <h3>{t(T_COOKIE_CONSENT_ARTICLE_TITLE)}</h3>
                    <p>{t(T_COOKIE_CONSENT_ARTICLE_CONTENT)}</p>
                </div>
                <div className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+'-section'}>

                    {cookieConsent === CookieConsent.UNDEFINED ? (
                        <>
                            <SubmitButton
                                className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+"-accept-all"}
                            >{t(T_COOKIE_CONSENT_ACCEPT_ALL_BUTTON_LABEL)}</SubmitButton>
                            <Button
                                className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+"-accept-mandatory"}
                                style={ButtonStyle.SECONDARY}
                                click={acceptMandatory}
                            >{t(T_COOKIE_CONSENT_ACCEPT_MANDATORY_BUTTON_LABEL)}</Button>
                        </>
                    ): <>

                        <>
                            {cookieConsent !== CookieConsent.ACCEPT_ALL ? null : (
                                <p><strong>{t(T_COOKIE_CONSENT_ACCEPT_ALL_SELECTED_TEXT)}</strong></p>
                            ) }

                            {cookieConsent !== CookieConsent.ACCEPT_MANDATORY ? null : (
                                <p><strong>{t(T_COOKIE_CONSENT_ACCEPT_MANDATORY_SELECTED_TEXT)}</strong></p>
                            ) }

                            {cookieConsent !== CookieConsent.ACCEPT_ALL ? (
                                <SubmitButton
                                    className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+"-accept-all"}
                                >{t(T_COOKIE_CONSENT_ACCEPT_ALL_BUTTON_LABEL)}</SubmitButton>
                            ) : null }

                            {cookieConsent !== CookieConsent.ACCEPT_MANDATORY ? (
                                <Button
                                    className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+"-accept-mandatory"}
                                    style={ButtonStyle.SECONDARY}
                                    click={acceptMandatory}
                                >{t(T_COOKIE_CONSENT_ACCEPT_MANDATORY_BUTTON_LABEL)}</Button>
                            ) : null}

                            {/*<Button*/}
                            {/*    className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+"-remove-consent"}*/}
                            {/*    style={ButtonStyle.SECONDARY}*/}
                            {/*    click={removeConsent}*/}
                            {/*>{t(T_COOKIE_CONSENT_REMOVE_CONSENT)}</Button>*/}

                        </>

                    </>}

                    { enableSettingsButton ? (
                        <LinkButton
                            className={COOKIE_CONSENT_ARTICLE_CLASS_NAME+"-settings"}
                            to={COOKIES_ROUTE}
                            label={t(T_COOKIE_CONSENT_SETTINGS_BUTTON_LABEL)} />
                    ) : null}

                </div>
            </Form>
        </div>
    );
}
